/**
 * =============================================================================
 * WATCH CARD COMPONENT - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 *
 * Alert card with semantic severity indicator.
 *
 * DESIGN SYSTEM:
 * - Card title: 16-18px
 * - Padding: 20-24px
 * - Semantic left indicator (Red/Amber/Green/Blue)
 * - Alert label at top (Replenishment/Reorder)
 * - Context pills showing filters
 * - No decorative borders
 */

import {
    Box,
    Stack,
    Text,
    Group,
    Skeleton,
} from '@mantine/core';
import {
    Warning,
    CaretRight,
    Lightning,
} from '@phosphor-icons/react';
import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type {
    WatchCardProps,
    WatchCardState,
    EditDialogConfig,
} from '../../types';
import { evaluateWatchCardAlert } from '../../api';
import ContextPills from '@components/common/ContextPills';

// =============================================================================
// DESIGN TOKENS
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 20,
    lg: 24,
} as const;

// Semantic colors - ONLY these colors for severity
const semanticColors = {
    critical: 'rgb(205, 101, 101)', // Red
    warning: 'rgb(219, 182, 88)', // Amber
    positive: 'rgb(45, 187, 136)', // Green
    info: 'rgb(1, 149, 255)', // Blue
} as const;

// Map priority to semantic severity
const priorityToSeverity: Record<string, keyof typeof semanticColors> = {
    urgent: 'critical',
    high: 'warning',
    medium: 'info',
    low: 'info',
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const WatchCard: React.FC<WatchCardProps> = memo(
    ({
        config,
        data: controlledData,
        isLoading: controlledLoading,
        error: controlledError,
        className = '',
        onClick,
        onEditClick,
        onActivateClick,
    }) => {
        const navigate = useNavigate();

        // Check if card is configured (defaults to true for backward compatibility)
        const isCardConfigured = config.isConfigured !== false;

        // Local state for uncontrolled mode
        const [state, setState] = useState<WatchCardState>({
            data: null,
            isLoading: isCardConfigured, // Only show loading if configured
            error: null,
            lastFetched: null,
        });

        // Edit dialog state
        const [editDialogOpen, setEditDialogOpen] = useState(false);
        const [isSaving, setIsSaving] = useState(false);

        // Determine if controlled or uncontrolled
        const isControlled = controlledData !== undefined;

        // Use controlled values if provided, otherwise use local state
        const displayData = isControlled ? controlledData : state.data;
        const displayLoading =
            controlledLoading ??
            (!isControlled && state.isLoading && isCardConfigured);
        const displayError = controlledError ?? state.error;

        // Fetch alert data on mount (uncontrolled mode only, and only if configured)
        useEffect(() => {
            if (!isControlled && isCardConfigured) {
                const loadData = async () => {
                    setState((prev) => ({
                        ...prev,
                        isLoading: true,
                        error: null,
                    }));
                    const result = await evaluateWatchCardAlert(config);
                    setState(result);
                };

                loadData();
            }
        }, [config, isControlled, isCardConfigured]);

        // Get semantic severity color
        const severity = config.priority
            ? priorityToSeverity[config.priority] || 'info'
            : 'info';
        const severityColor = semanticColors[severity];

        // Handle card click - navigate to alert page (only if we have data)
        const handleClick = useCallback(() => {
            if (onClick) {
                onClick();
            } else if (displayData?.checklistItemId) {
                // Only navigate if we have a checklistItemId
                const path = config.onClickPath.replace(
                    ':alertId',
                    displayData.checklistItemId,
                );
                navigate(path);
            }
            // If no data, clicking on card does nothing (user should click "Activate Story")
        }, [onClick, navigate, config.onClickPath, displayData]);

        // Handle edit click (stop propagation to prevent card click)
        const handleEditClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                if (onEditClick) {
                    onEditClick();
                } else {
                    setEditDialogOpen(true);
                }
            },
            [onEditClick],
        );

        // Handle edit save
        const handleEditSave = useCallback(async (data: unknown) => {
            setIsSaving(true);
            console.log('[WatchCard] Saving edits:', data);
            await new Promise((resolve) => setTimeout(resolve, 500));
            setIsSaving(false);
            setEditDialogOpen(false);
        }, []);

        // Build edit dialog config
        const editDialogConfig: EditDialogConfig | null = useMemo(() => {
            if (!config.isEditable || !config.editConfig) return null;

            return {
                entityId: config.id,
                entityType: 'watch-card',
                entityLabel:
                    displayData?.heading ||
                    config.fallback?.heading ||
                    'Watch Card',
                reference: {
                    ...config.editConfig.reference,
                    title:
                        config.editConfig.reference?.title ||
                        displayData?.heading ||
                        config.fallback?.heading ||
                        'Alert',
                    description:
                        config.editConfig.reference?.description ||
                        displayData?.summary ||
                        config.fallback?.summary,
                },
                agentConfig: config.editConfig.agentConfig,
            };
        }, [config, displayData]);

        // Handle activate click for unconfigured cards
        const handleActivateClick = useCallback(
            (e: React.MouseEvent) => {
                e.stopPropagation();
                if (onActivateClick) {
                    onActivateClick();
                } else {
                    // Default behavior - can be configured later
                    console.log(
                        '[WatchCard] Activate Story clicked for card:',
                        config.id,
                    );
                }
            },
            [onActivateClick, config.id],
        );

        // Render unconfigured state with "Activate Story" CTA
        if (!isCardConfigured) {
            return (
                <Box
                    className={`relative ${className}`}
                    style={{
                        backgroundColor: 'white',
                        borderRadius: 12,
                        padding: spacing.md,
                        paddingLeft: spacing.lg,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: 140,
                    }}
                >
                    {/* Muted severity indicator - left bar (gray for unconfigured) */}
                    <Box
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            backgroundColor: 'rgba(148, 163, 184, 0.4)',
                            borderRadius: '12px 0 0 12px',
                        }}
                    />

                    {/* Header - Category label if provided */}
                    {config.categoryLabel && (
                        <Text
                            className="tracking-wider text-slate-300 uppercase"
                            style={{
                                fontSize: 10,
                                fontWeight: 600,
                                letterSpacing: '0.05em',
                                marginBottom: spacing.xs,
                            }}
                        >
                            {config.categoryLabel}
                        </Text>
                    )}

                    {/* Centered CTA - Non-clickable "Coming Soon!" */}
                    <Box
                        style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box
                            style={{
                                backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                color: 'rgb(99, 102, 241)',
                                fontWeight: 600,
                                fontSize: 13,
                                padding: '8px 16px',
                                borderRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                cursor: 'default',
                            }}
                        >
                            <Lightning size={16} weight="fill" />
                            Coming Soon!
                        </Box>
                    </Box>
                </Box>
            );
        }

        return (
            <>
                <Box
                    className={`group relative cursor-pointer ${className}`}
                    style={{
                        backgroundColor: 'white',
                        borderRadius: 12,
                        padding: spacing.md,
                        paddingLeft: spacing.lg,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden',
                    }}
                    onClick={handleClick}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow =
                            '0 4px 12px rgba(0,0,0,0.08)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow =
                            '0 1px 3px rgba(0,0,0,0.04)';
                        e.currentTarget.style.transform = 'translateY(0)';
                    }}
                >
                    {/* Semantic severity indicator - left bar */}
                    <Box
                        style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            backgroundColor: severityColor,
                            borderRadius: '12px 0 0 12px',
                        }}
                    />

                    {/* Header row: Alert Label (Replenishment/Reorder) from data + Edit */}
                    <Group
                        className="items-start justify-between"
                        style={{ marginBottom: spacing.xs }}
                    >
                        {/* Alert label from data at top - 10px uppercase */}
                        {(displayData?.alertLabel || config.categoryLabel) && (
                            <Text
                                className="tracking-wider text-slate-400 uppercase"
                                style={{
                                    fontSize: 10,
                                    fontWeight: 600,
                                    letterSpacing: '0.05em',
                                }}
                            >
                                {displayData?.alertLabel ||
                                    config.categoryLabel}
                            </Text>
                        )}

                        {/* Edit button - temporarily disabled, will add edit flow later */}
                        {/* {config.isEditable && (
                        <Tooltip label="Edit alert" position="top">
                            <ActionIcon
                                variant="subtle"
                                color="gray"
                                size="xs"
                                onClick={handleEditClick}
                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <PencilSimple size={12} weight="bold" />
                            </ActionIcon>
                        </Tooltip>
                    )} */}
                    </Group>

                    {/* Content */}
                    {displayLoading ? (
                        <Stack style={{ gap: spacing.xs }}>
                            <Skeleton height={18} width="80%" radius={4} />
                            <Skeleton height={14} width="60%" radius={4} />
                        </Stack>
                    ) : displayError && !displayData ? (
                        <Group
                            style={{
                                gap: spacing.xs,
                                color: semanticColors.critical,
                            }}
                        >
                            <Warning size={16} weight="fill" />
                            <Text style={{ fontSize: 13 }}>Failed to load</Text>
                        </Group>
                    ) : !displayData ? (
                        // No data available - show "Activate Story" button instead of fallback text
                        <Box
                            style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: 60,
                            }}
                        >
                            <Box
                                onClick={handleActivateClick}
                                style={{
                                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                    color: 'rgb(99, 102, 241)',
                                    fontWeight: 600,
                                    fontSize: 13,
                                    padding: '8px 16px',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        'rgba(99, 102, 241, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                        'rgba(99, 102, 241, 0.08)';
                                }}
                            >
                                <Lightning size={16} weight="fill" />
                                Activate Story
                            </Box>
                        </Box>
                    ) : (
                        <Stack style={{ gap: 4 }}>
                            {/* Heading - 15px, semibold */}
                            <Text
                                className="line-clamp-2 text-slate-900"
                                style={{
                                    fontSize: 15,
                                    fontWeight: 600,
                                    lineHeight: 1.3,
                                }}
                            >
                                {displayData.heading}
                            </Text>

                            {/* Context Pills - filters from data */}
                            <ContextPills filters={displayData.filters} />

                            {/* Summary - muted, only if no filters */}
                            {!displayData.filters && displayData.summary && (
                                <Text
                                    className="line-clamp-2 text-slate-400"
                                    style={{ fontSize: 12, lineHeight: 1.4 }}
                                >
                                    {displayData.summary}
                                </Text>
                            )}
                        </Stack>
                    )}

                    {/* Action hint - minimal (only show when we have data) */}
                    {displayData && (
                        <Group
                            style={{
                                gap: 4,
                                marginTop: spacing.sm,
                                paddingTop: spacing.xs,
                                borderTop: '1px solid rgba(0,0,0,0.04)',
                            }}
                        >
                            <Text
                                className="text-slate-500"
                                style={{ fontSize: 12, fontWeight: 500 }}
                            >
                                View details
                            </Text>
                            <CaretRight
                                size={12}
                                weight="bold"
                                className="text-slate-400"
                            />
                        </Group>
                    )}
                </Box>

                {/* Edit Dialog - temporarily disabled, will add edit flow later */}
                {/* {config.isEditable && editDialogConfig && (
                <EditDialog
                    opened={editDialogOpen}
                    config={editDialogConfig}
                    onClose={() => setEditDialogOpen(false)}
                    onSave={handleEditSave}
                    isSaving={isSaving}
                />
            )} */}
            </>
        );
    },
);

WatchCard.displayName = 'WatchCard';

export default WatchCard;
